import { getBandColor, RiskBand } from 'components/RiskMatrix/bands';
import { getColor, getLabel } from 'containers/RiskAssessmentsDetails/RiskMatrix/styles';

import type { CellValue, Column, RiskRatingValue, Row } from './types';

export const generateRowId = (): string => `row-${crypto.randomUUID()}`;

export const generateColumnId = (): string => `col-${crypto.randomUUID()}`;

export const isRiskRatingValue = (v: CellValue): v is RiskRatingValue =>
  v !== null && typeof v === 'object' && 'likelihood' in v && 'severity' in v;

// Reconcile rows after a column or matrix-size change:
// - keep values for columns that still exist (by id), drop the rest
// - new columns default to null
// - Risk-rating cells clamp out-of-range likelihood/severity to null when the matrix shrinks
export const reconcileRows = (
  rows: Row[],
  newColumns: Column[],
  maxLikelihood: number,
  maxSeverity: number
): Row[] =>
  rows.map((row) => {
    const newValues: Record<string, CellValue> = {};
    newColumns.forEach((col) => {
      const existing = row.values[col.id] ?? null;
      if (col.type === 'riskRating' && isRiskRatingValue(existing)) {
        const likelihood =
          existing.likelihood !== null && existing.likelihood <= maxLikelihood
            ? existing.likelihood
            : null;
        const severity =
          existing.severity !== null && existing.severity <= maxSeverity
            ? existing.severity
            : null;
        newValues[col.id] = { likelihood, severity };
      } else {
        newValues[col.id] = existing;
      }
    });
    return { ...row, values: newValues };
  });

// The node's legend and its stored labels are plain strings, so this narrows to the shared
// palette and keeps Medium as the fallback for a band it does not recognise.
const isBand = (label: string): label is RiskBand => Object.values(RiskBand).includes(label as RiskBand);

export const getLegendColor = (label: string): { bg: string; color: string } =>
  getBandColor(isBand(label) ? label : RiskBand.Medium);

export const getRiskCellInfo = (
  severity: number,
  likelihood: number,
  matrixRows: number,
  matrixColumns: number
): { score: number; bg: string; color: string; label: string } => {
  const score = likelihood * severity;
  const { bg, color } = getColor(severity, likelihood, matrixRows, matrixColumns);
  const label = getLabel(severity, likelihood, matrixRows, matrixColumns);
  return { score, bg, color, label };
};
